/**
 * useRetakeFeeLookup — encapsulates the lookup state machine for the
 * standalone form page. The hook owns:
 *   - loading flag (single in-flight request)
 *   - error message (server-side EC: -1 → toast/inline text)
 *   - result (the tagged status payload)
 *
 * Inputs are validated client-side (12 numeric chars) before the
 * request fires, so the BE only sees well-formed CCCDs in the happy
 * path.
 */

import { useCallback, useState } from 'react';
import { lookupRetakeFeeApi } from '../services/retakeFeeApi';
import type { RetakeFeeLookupResult } from '../types';

const CCCD_PATTERN = /^\d{12}$/;

export type UseRetakeFeeLookupReturn = {
  loading: boolean;
  error: string | null;
  result: RetakeFeeLookupResult | null;
  lookup: (cccd: string) => Promise<void>;
  reset: () => void;
};

export const useRetakeFeeLookup = (): UseRetakeFeeLookupReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RetakeFeeLookupResult | null>(null);

  const lookup = useCallback(async (cccd: string) => {
    const trimmed = (cccd || '').trim();
    if (!CCCD_PATTERN.test(trimmed)) {
      setError('Số CCCD phải gồm đúng 12 chữ số.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await lookupRetakeFeeApi(trimmed);
      if (resp.EC !== 0 || !resp.DT) {
        setError(resp.EM || 'Không tra cứu được. Vui lòng thử lại sau.');
        setResult(null);
        return;
      }
      setResult(resp.DT);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } }; message?: string };
      setError(
        e?.response?.data?.EM ||
          e?.message ||
          'Mạng đang chậm. Vui lòng thử lại sau.',
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setResult(null);
  }, []);

  return { loading, error, result, lookup, reset };
};
